import { AxiosRequestConfig } from 'axios';
import FileSaver from 'file-saver';
import { create } from 'zustand';
import { ButtonStatusVariant } from '../../components/ButtonWithFeddback';
import { Catalog } from '../../modules/catalogs/catalog.types';
import {
  ExportMethod,
  ExportType,
  OnSubmitCatalogModal,
  OpenCatalogModal,
} from '../../modules/catalogs/components/CatalogExportModal/types/catalogExportModal.types';
import { downloadXlsx } from '../../modules/xlsx/downloadXlsx';
import { restApi } from '../../services/api';
import snackbarStore from '../Snackbar.store';

interface CatalogModalState {
  isModalOpen: boolean;
  isLoading: boolean;
  catalog: Catalog | null;
  activeFilters: Record<string, string | string[]> | null;
  initialClassificationTypeId: string | null;
  buttonStatus: ButtonStatusVariant;
  openModal: (params: OpenCatalogModal) => void;
  closeModal: () => void;
  setIsLoading: (value: boolean) => void;
  onSubmit: (params: OnSubmitCatalogModal) => Promise<void>;
  reset: () => void;
}

export const useCatalogModalStore = create<CatalogModalState>((set, get) => ({
  isModalOpen: false,
  isLoading: false,
  catalog: null,
  activeFilters: null,
  initialClassificationTypeId: null,
  buttonStatus: 'normal',

  openModal: (params: OpenCatalogModal) => {
    const { catalog, activeFilters, initialClassificationTypeId } = params;
    return set({
      catalog,
      isModalOpen: true,
      activeFilters: activeFilters ?? null,
      initialClassificationTypeId: initialClassificationTypeId ?? null,
    });
  },

  closeModal: () => {
    set({ isModalOpen: false });
  },

  setIsLoading: (value) => {
    set({ isLoading: value });
  },

  onSubmit: async (params: OnSubmitCatalogModal) => {
    const { values, messages, isAdminSeller } = params;
    const state = get();
    set({ isLoading: true });

    if (!state.catalog) {
      return;
    }

    if (values.exportType === ExportType.XLSX) {
      try {
        await downloadXlsx({
          url: `/catalogs/${state.catalog.id}/import/xlsx`,
          fileName: `${state.catalog.name}.xlsx`,
        });
        set({ buttonStatus: 'success' });
      } catch {
        snackbarStore.handleMessage({
          message: messages.onErrorMessage,
          type: 'error',
        });
      }
      get().reset();
      return;
    }

    const isDownload = values.exportMethod === ExportMethod.DOWNLOAD;
    const isPdf = values.exportType === ExportType.PDF;
    const endpoint = isDownload ? 'generate-pdf' : 'generate-pdf-email';

    const axiosConfig: AxiosRequestConfig | undefined =
      isDownload && isPdf
        ? {
            responseType: 'blob',
          }
        : undefined;

    const filterValues = {
      ...values,
      ...(isPdf ? { active: 'true' } : {}),
      ...(isPdf && state.activeFilters ? state.activeFilters : {}),
      isAdminSeller,
    };

    let response;

    if (isDownload) {
      response = await restApi.get<Blob | any>(
        `catalogs/${state.catalog.id}/${endpoint}`,
        filterValues,
        axiosConfig,
      );
    }

    if (!isDownload) {
      response = await restApi.post(`catalogs/${state.catalog.id}/${endpoint}`, {
        ...filterValues,
        isAdminSeller: String(!!isAdminSeller),
      });
    }

    if (response?.ok && isDownload && isPdf) {
      const buffer = response?.data as BlobPart;
      FileSaver(new Blob([buffer], { type: 'application/pdf' }), `${state.catalog.name}.pdf`);
      set({ buttonStatus: 'success' });
    }

    if (response?.ok && !isDownload) {
      snackbarStore.handleMessage({
        message: messages.onSuccessMessage,
        type: 'success',
      });
      set({ buttonStatus: 'success' });
    }

    if (!response?.ok) {
      snackbarStore.handleMessage({
        message: messages.onErrorMessage,
        type: 'error',
      });
    }

    get().reset();
  },

  reset: () => {
    set({
      isLoading: false,
      catalog: null,
      isModalOpen: false,
      buttonStatus: 'normal',
      activeFilters: null,
      initialClassificationTypeId: null,
    });
  },
}));

const catalogModalStore = {
  get isModalOpen() {
    return useCatalogModalStore.getState().isModalOpen;
  },
  get isLoading() {
    return useCatalogModalStore.getState().isLoading;
  },
  get catalog() {
    return useCatalogModalStore.getState().catalog;
  },
  get buttonStatus() {
    return useCatalogModalStore.getState().buttonStatus;
  },
  openModal: (params: OpenCatalogModal) => useCatalogModalStore.getState().openModal(params),
  closeModal: () => useCatalogModalStore.getState().closeModal(),
  setIsLoading: (value: boolean) => useCatalogModalStore.getState().setIsLoading(value),
  onSubmit: (params: OnSubmitCatalogModal) => useCatalogModalStore.getState().onSubmit(params),
  reset: () => useCatalogModalStore.getState().reset(),
};

export default catalogModalStore;
